import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { Bell, TrendingUp, FileText, AlertCircle, Users, User, CreditCard, Eye, BarChart3, PieChart, Activity, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { formatCurrency as marketFormatCurrency } from '@/lib/market';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserDashboard = ({ user, onLogout }) => {
  const [portfolio, setPortfolio] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [familyData, setFamilyData] = useState(null);
  const [ownerData, setOwnerData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedView, setSelectedView] = useState('overview'); // overview, family, accounts
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Fetch questionnaire
      const questionnaireRes = await axios.get(`${API}/questionnaire/me`, config);
      setQuestionnaire(questionnaireRes.data);

      // Fetch user's family and hierarchy information
      await fetchUserHierarchy(config);

      // Fetch notifications
      try {
        const notificationsRes = await axios.get(`${API}/notifications/me`, config);
        setNotifications(notificationsRes.data || []);
      } catch (error) {
        console.log('No notifications available');
        setNotifications([]);
      }

    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserHierarchy = async (config) => {
    try {
      // Use new user hierarchy endpoint
      const hierarchyRes = await axios.get(`${API}/user/hierarchy`, config);
      const hierarchyData = hierarchyRes.data;

      setFamilyData(hierarchyData.family);
      setOwnerData(hierarchyData.owner);
      setAccounts(hierarchyData.accounts || []);

      // Also fetch legacy portfolio if available
      try {
        const portfolioRes = await axios.get(`${API}/portfolios/user/${user.id}`, config);
        setPortfolio(portfolioRes.data);
      } catch (error) {
        console.log('No legacy portfolio data available');
      }

    } catch (error) {
      console.error('Error fetching user hierarchy:', error);
      toast.error('Failed to load your investment data');
    }
  };

  const formatCurrency = (value) => marketFormatCurrency(value, user?.market);

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-8">
        {/* Hero Section with Family Information */}
        <div className="relative overflow-hidden rounded-2xl p-8 bg-white border border-gray-100">
          <div className="relative z-10">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-3" style={{fontFamily: "'PerfectNineties', serif"}}>
                  Welcome back, {user?.full_name?.split(' ')[0]}!
                </h1>
                {familyData ? (
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-ugmonk-dark" />
                    <p className="text-lg text-gray-600">
                      {familyData.family_name} • Managed by {familyData.rm_name || 'Sanctum Wealth'}
                    </p>
                  </div>
                ) : (
                  <p className="text-lg text-gray-600">Your personalized investment dashboard</p>
                )}
              </div>
              
              <button
                onClick={() => fetchUserData()}
                className="p-2 rounded-lg bg-white/80 border border-gray-200 hover:bg-gray-50 transition-colors"
                title="Refresh data from Wealth Spectrum"
              >
                <RefreshCw size={16} className="text-gray-600" />
              </button>
            </div>
            
            {/* Family-level Stats */}
            {familyData && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 stagger-in">
                <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 border border-white/50 card-glow">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-[#fffdf0]">
                      <Users size={20} className="text-ugmonk-dark" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Family AUM</p>
                      <p className="text-lg font-bold text-gray-900">
                        {formatCurrency(familyData.total_aum)}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 border border-white/50 card-glow">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-[#fffdf0]">
                      <User size={20} className="text-ugmonk-dark" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Your AUM</p>
                      <p className="text-lg font-bold text-gray-900">
                        {ownerData ? formatCurrency(ownerData.total_aum) : 'Loading...'}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 border border-white/50 card-glow">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-100">
                      <CreditCard size={20} className="text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Accounts</p>
                      <p className="text-lg font-bold text-gray-900">
                        {accounts.length}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 border border-white/50 card-glow">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-gray-100">
                      <FileText size={20} className="text-gray-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Risk Profile</p>
                      <p className="text-lg font-bold text-gray-900">
                        {questionnaire?.risk_profile || ownerData?.risk_category || 'Pending'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-[#ffd87a]/20 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-[#1a1a1a]/20 to-transparent rounded-full blur-3xl"></div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setSelectedView('overview')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              selectedView === 'overview'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setSelectedView('family')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              selectedView === 'family'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Family Details
          </button>
          <button
            onClick={() => setSelectedView('accounts')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              selectedView === 'accounts'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            My Accounts
          </button>
        </div>

        {/* Content based on selected view */}
        {selectedView === 'overview' && (
          <>
            {/* Action Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {/* Questionnaire Card */}
          {!questionnaire?.completed ? (
            <div className="bg-gradient-to-br from-[#fffdf0] to-[#fffdf0]/50 rounded-xl p-6 border border-ugmonk-border">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-[#fffdf0]">
                  <AlertCircle className="text-ugmonk-dark" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Complete Your Risk Profile</h3>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                    Help us understand your investment goals and risk tolerance to provide personalized recommendations tailored to your financial objectives.
                  </p>
                  <button
                    onClick={() => navigate('/questionnaire')}
                    className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black px-6 py-3 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-2"
                  >
                    Start Assessment
                    <TrendingUp size={16} />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-xl p-6 border border-green-200">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-green-200">
                  <FileText className="text-green-700" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Risk Profile Complete</h3>
                  <p className="text-sm text-gray-600 mb-2">
                    Profile: <span className="font-semibold text-green-700">{questionnaire.risk_profile}</span>
                  </p>
                  <p className="text-sm text-gray-600 mb-4">
                    Score: {questionnaire.risk_score}/100
                  </p>
                  <button
                    onClick={() => navigate('/risk-profile-results')}
                    className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black px-6 py-3 rounded-lg text-sm font-medium transition-colors"
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Portfolio Card */}
          <div className="bg-gradient-to-br from-[#fffdf0] to-[#fffdf0]/50 rounded-xl p-6 border border-ugmonk-border">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg bg-[#fffdf0]">
                <TrendingUp className="text-ugmonk-dark" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Your Portfolio</h3>
                {portfolio && portfolio.total_market_value > 0 ? (
                  <>
                    <p className="text-sm text-gray-600 mb-2">
                      Current Value: <span className="font-semibold text-ugmonk-dark">{formatCurrency(portfolio.total_market_value)}</span>
                    </p>
                    <p className="text-sm text-gray-600 mb-4">
                      Holdings: {portfolio.holdings?.length || 0} securities
                    </p>
                    <button
                      onClick={() => navigate('/dashboard')}
                      className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black px-6 py-3 rounded-lg text-sm font-medium transition-colors"
                    >
                      View Portfolio
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                      Your portfolio information will appear here once your investments are uploaded by your relationship manager.
                    </p>
                    <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3 border border-gray-200">
                      📞 Contact your RM for portfolio setup
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button 
              onClick={() => navigate('/risk-profile-results')}
              className="flex flex-col items-center gap-2 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <FileText size={20} className="text-ugmonk-dark" />
              <span className="text-sm font-medium text-gray-700">Risk Profile</span>
            </button>
            
            <button 
              onClick={() => setSelectedView('accounts')}
              className="flex flex-col items-center gap-2 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <CreditCard size={20} className="text-ugmonk-dark" />
              <span className="text-sm font-medium text-gray-700">My Accounts</span>
            </button>
            
            <button 
              onClick={() => setSelectedView('family')}
              className="flex flex-col items-center gap-2 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <Users size={20} className="text-ugmonk-dark" />
              <span className="text-sm font-medium text-gray-700">Family</span>
            </button>
            
            <button className="flex flex-col items-center gap-2 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors opacity-50 cursor-not-allowed">
              <Bell size={20} className="text-gray-400" />
              <span className="text-sm font-medium text-gray-400">Reports</span>
            </button>
          </div>
        </div>
        </>
        )}

        {/* Family Details View */}
        {selectedView === 'family' && familyData && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">Family Information</h2>
              
              {/* Family Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Family Details</h3>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm text-gray-500">Family Name</span>
                      <p className="font-medium text-gray-900">{familyData.family_name}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Family Head</span>
                      <p className="font-medium text-gray-900">{familyData.family_head}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Family Type</span>
                      <p className="font-medium text-gray-900">{familyData.family_type || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Primary Residence</span>
                      <p className="font-medium text-gray-900">{familyData.primary_residence || 'N/A'}</p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Investment Details</h3>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm text-gray-500">Total Family AUM</span>
                      <p className="text-2xl font-bold text-ugmonk-dark">{formatCurrency(familyData.total_aum)}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Relationship Manager</span>
                      <p className="font-medium text-gray-900">{familyData.rm_name || 'Sanctum Wealth'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Account Status</span>
                      <span className="inline-block bg-green-100 text-green-800 text-sm px-2 py-1 rounded-full">
                        {familyData.status || 'Active'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Your Information */}
            {ownerData && (
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Your Profile</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <span className="text-sm text-gray-500">Name</span>
                    <p className="font-medium text-gray-900">{ownerData.owner_name}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">PAN</span>
                    <p className="font-medium text-gray-900">{ownerData.pan}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Mobile</span>
                    <p className="font-medium text-gray-900">{ownerData.mobile}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Email</span>
                    <p className="font-medium text-gray-900">{ownerData.email || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Your AUM</span>
                    <p className="text-xl font-bold text-ugmonk-dark">{formatCurrency(ownerData.total_aum)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">KYC Status</span>
                    <span className="inline-block bg-green-100 text-green-800 text-sm px-2 py-1 rounded-full">
                      {ownerData.kyc_status || 'Completed'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Accounts View */}
        {selectedView === 'accounts' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-gray-900">My Accounts</h2>
                <span className="text-sm bg-[#fffdf0] text-ugmonk-dark px-3 py-1 rounded-full">
                  {accounts.length} Account{accounts.length !== 1 ? 's' : ''}
                </span>
              </div>

              {accounts.length > 0 ? (
                <div className="grid gap-4">
                  {accounts.map((account) => (
                    <div
                      key={account.id}
                      className="border border-gray-200 rounded-lg p-6 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">{account.account_name}</h3>
                          <p className="text-sm text-gray-500">Account #{account.account_number}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-ugmonk-dark">
                            {formatCurrency(account.total_value)}
                          </p>
                          <p className="text-sm text-gray-500">Current Value</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <span className="text-sm text-gray-500">Account Type</span>
                          <p className="font-medium text-gray-900 capitalize">{account.account_type}</p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500">Advisor</span>
                          <p className="font-medium text-gray-900">{account.advisor_name}</p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500">Status</span>
                          <div className="flex items-center gap-2">
                            {account.is_held_away ? (
                              <span className="inline-block bg-[#fffdf0] text-ugmonk-darker text-sm px-2 py-1 rounded-full">
                                Held Away
                              </span>
                            ) : (
                              <span className="inline-block bg-green-100 text-green-800 text-sm px-2 py-1 rounded-full">
                                {account.status || 'Active'}
                              </span>
                            )}
                          </div>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500">Last Updated</span>
                          <p className="font-medium text-gray-900">
                            {new Date(account.as_on_date).toLocaleDateString('en-IN')}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex gap-2">
                          <button className="flex items-center gap-2 px-4 py-2 bg-[#1a1a1a] text-white rounded-lg hover:bg-[#f5cc6a] transition-colors text-sm">
                            <Eye size={16} />
                            View Details
                          </button>
                          <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm">
                            <BarChart3 size={16} />
                            Performance
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <CreditCard className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Accounts Found</h3>
                  <p className="text-gray-600">Your account information will appear here once set up by your RM.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notifications Section (shown in overview only) */}
        {selectedView === 'overview' && notifications.length > 0 && (
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg bg-[#fffdf0]">
                <Bell size={20} className="text-ugmonk-dark" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Recent Updates</h2>
            </div>
            <div className="space-y-4">
              {notifications.slice(0, 3).map((notif) => (
                <div
                  key={notif.id}
                  className={`p-4 rounded-lg cursor-pointer transition-all border ${
                    notif.read 
                      ? 'bg-gray-50 border-gray-200 hover:bg-gray-100' 
                      : 'bg-[#ffd87a]/5 border-ugmonk-border hover:bg-[#fffdf0]'
                  }`}
                  onClick={() => notif.report_id && navigate(`/report/${notif.report_id}`)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-1 rounded-full ${notif.read ? 'bg-gray-300' : 'bg-[#ffd87a]'} w-2 h-2 mt-2`}></div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 mb-1">{notif.title}</h3>
                      <p className="text-sm text-gray-600 mb-2">{notif.message}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(notif.created_at).toLocaleDateString('en-IN', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Portfolio Detail Section (shown in overview only) */}
        {selectedView === 'overview' && portfolio && portfolio.total_market_value > 0 && (
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Portfolio Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-[#fffdf0] to-[#fffdf0]/50 rounded-lg p-4 border border-ugmonk-border">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[#fffdf0]">
                    <TrendingUp size={20} className="text-ugmonk-dark" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Portfolio Value</p>
                    <p className="text-xl font-bold text-gray-900">
                      {formatCurrency(portfolio.total_market_value)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-lg p-4 border border-green-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-200">
                    <TrendingUp size={20} className="text-green-700" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Total Gain</p>
                    <p className="text-xl font-bold text-green-700">
                      +{formatCurrency(portfolio.total_unrealized_gain || 0)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#fffdf0] to-[#fffdf0]/50 rounded-lg p-4 border border-ugmonk-border">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[#fffdf0]">
                    <FileText size={20} className="text-ugmonk-dark" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Annualized Return</p>
                    <p className="text-xl font-bold text-gray-900">
                      {portfolio.total_annualized_yield?.toFixed(2) || '0.00'}%
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Portfolio Actions */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-left"
                >
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">View Detailed Portfolio</h3>
                    <p className="text-sm text-gray-600">Complete breakdown of your holdings</p>
                  </div>
                  <TrendingUp size={16} className="text-ugmonk-dark" />
                </button>

                <button
                  onClick={() => navigate('/risk-profile-results')}
                  className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-left"
                >
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">View Recommendations</h3>
                    <p className="text-sm text-gray-600">Investment recommendations from your RM</p>
                  </div>
                  <FileText size={16} className="text-ugmonk-dark" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Educational Content (shown in overview only) */}
        {selectedView === 'overview' && (
          <div className="bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl p-6 border border-gray-200">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-lg bg-[#fffdf0]">
                <AlertCircle className="text-ugmonk-dark" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Investment Insights</h3>
                <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                  Stay informed with market updates and personalized investment insights tailored to your portfolio and risk profile.
                  {familyData && ` Your family's total AUM of ${formatCurrency(familyData.total_aum)} is managed by ${familyData.rm_name || 'our expert team'}.`}
                </p>
                <div className="text-xs text-gray-500 bg-white rounded-lg p-3 border border-gray-200">
                  💡 <strong>Tip:</strong> Regular portfolio reviews help you stay aligned with your financial goals. Your next review is scheduled based on your risk profile.
                  {ownerData?.risk_category && ` As a ${ownerData.risk_category.toLowerCase()} investor, we recommend quarterly reviews.`}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default UserDashboard;
